/**
 * useMediaAssetsByIds – fetch multiple media_assets by UUID array.
 * Preserves input order. Returns resolved URLs (320/640).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { resolveAssetUrl } from '@/lib/media/mediaApi';
import type { MediaAsset } from '@/lib/media/mediaTypes';

export function useMediaAssetsByIds(ids: string[]) {
  const stableKey = ids.length > 0 ? ids.join(',') : '';

  const { data: assets = [], isLoading } = useQuery({
    queryKey: ['media-assets-by-ids', stableKey],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('media_assets')
        .select('*')
        .in('id', ids);
      if (error) throw error;
      // Reorder to match input ids order
      const map = new Map((data ?? []).map((r) => [r.id, r as unknown as MediaAsset]));
      return ids.map((id) => map.get(id)).filter(Boolean) as MediaAsset[];
    },
    staleTime: 5 * 60_000,
  });

  return {
    assets,
    urls320: assets.map((a) => resolveAssetUrl(a, 320)).filter(Boolean) as string[],
    urls640: assets.map((a) => resolveAssetUrl(a, 640)).filter(Boolean) as string[],
    isLoading,
  };
}
